// src/api/queues/queueApi.ts
import axios from "axios";

import type {
	EscalateResult,
	FailedJobDetail,
	FailedJobsPage,
	FailedJobSummary,
	HorizonStatus,
	QueueOverview,
	RetryAllResult,
	RetryResult,
} from "../../types/queues/QueueTypes";
import { http } from "../../lib/http/client";

// Thrown when a service answers 200 with a body outside the pinned envelope — never rendered as "no data".
export class QueueEnvelopeError extends Error {
	constructor(public readonly route: string, detail: string) {
		super(`Unexpected ${route} response: ${detail}`);
		this.name = "QueueEnvelopeError";
	}
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);

const isStringArray = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === "string");

const requireKeys = (route: string, value: unknown, keys: string[]): Record<string, unknown> => {
	if (!isRecord(value)) throw new QueueEnvelopeError(route, "body is not an object");
	const missing = keys.filter((k) => !(k in value));
	if (missing.length > 0) throw new QueueEnvelopeError(route, `missing ${missing.join(", ")}`);
	return value;
};

const HORIZON_STATUSES: HorizonStatus[] = ["running", "paused", "inactive"];

export const parseOverview = (body: unknown): QueueOverview => {
	const route = "GET overview";
	const o = requireKeys(route, body, ["service", "horizon", "queues", "metrics", "generated_at"]);
	const horizon = requireKeys(route, o.horizon, ["status", "masters"]);
	if (!HORIZON_STATUSES.includes(horizon.status as HorizonStatus)) {
		throw new QueueEnvelopeError(route, `horizon.status ${String(horizon.status)}`);
	}
	if (!Array.isArray(horizon.masters) || !Array.isArray(o.queues)) throw new QueueEnvelopeError(route, "masters/queues not arrays");
	requireKeys(route, o.metrics, ["jobs_per_minute", "processed_last_hour", "failed_last_hour", "recent_failed_total"]);
	o.queues.forEach((q) => requireKeys(route, q, ["name", "connection", "length", "wait_seconds", "processes"]));
	horizon.masters.forEach((m) => {
		const master = requireKeys(route, m, ["name", "pid", "status", "supervisors"]);
		if (!Array.isArray(master.supervisors)) throw new QueueEnvelopeError(route, "supervisors not an array");
		master.supervisors.forEach((s) => requireKeys(route, s, ["name", "processes", "queues", "status"]));
	});
	return o as unknown as QueueOverview;
};

const FAILED_KEYS = ["id", "name", "queue", "connection", "failed_at", "attempts", "exception_class", "exception_summary", "retried_to"];

const parseFailedSummary = (route: string, item: unknown): FailedJobSummary => {
	const f = requireKeys(route, item, FAILED_KEYS);
	if (!isStringArray(f.retried_to)) throw new QueueEnvelopeError(route, "retried_to not a string array");
	return f as unknown as FailedJobSummary;
};

export const parseFailedPage = (body: unknown): FailedJobsPage => {
	const route = "GET failed";
	const p = requireKeys(route, body, ["data", "meta"]);
	if (!Array.isArray(p.data)) throw new QueueEnvelopeError(route, "data not an array");
	requireKeys(route, p.meta, ["total", "page", "per_page"]);
	return { data: p.data.map((item) => parseFailedSummary(route, item)), meta: p.meta as FailedJobsPage["meta"] };
};

export const parseFailedDetail = (body: unknown): FailedJobDetail => {
	const route = "GET failed/{id}";
	const f = requireKeys(route, body, [...FAILED_KEYS, "tags", "payload_command"]);
	if (!isStringArray(f.retried_to) || !isStringArray(f.tags)) throw new QueueEnvelopeError(route, "retried_to/tags not string arrays");
	return f as unknown as FailedJobDetail;
};

export const parseRetry = (body: unknown): RetryResult =>
	requireKeys("POST failed/{id}/retry", body, ["id", "retried", "new_id"]) as unknown as RetryResult;

export const parseRetryAll = (body: unknown): RetryAllResult => {
	const r = requireKeys("POST failed/retry-all", body, ["retried", "ids"]);
	if (!isStringArray(r.ids)) throw new QueueEnvelopeError("POST failed/retry-all", "ids not a string array");
	return r as unknown as RetryAllResult;
};

export const parseEscalate = (body: unknown): EscalateResult =>
	requireKeys("POST escalate", body, ["incident_id", "reported"]) as unknown as EscalateResult;

export interface FailedQuery {
	queue?: string;
	page?: number;
	per_page?: number;
}

// One client per service: every backend exposes the same routes under its own `/api` base.
export const queueApi = (baseUrl: string) => {
	const url = (path: string) => `${baseUrl}/admin/queue${path}`;

	return {
		getOverview: async (): Promise<QueueOverview> => {
			const response = await http.get(url("/overview"));
			return parseOverview(response.data);
		},
		getFailed: async (query: FailedQuery = {}): Promise<FailedJobsPage> => {
			const response = await http.get(url("/failed"), { params: query });
			return parseFailedPage(response.data);
		},
		getFailedJob: async (id: string): Promise<FailedJobDetail> => {
			const response = await http.get(url(`/failed/${encodeURIComponent(id)}`));
			return parseFailedDetail(response.data);
		},
		retry: async (id: string): Promise<RetryResult> => {
			const response = await http.post(url(`/failed/${encodeURIComponent(id)}/retry`), {});
			return parseRetry(response.data);
		},
		retryAll: async (body: { queue?: string; limit?: number } = {}): Promise<RetryAllResult> => {
			const response = await http.post(url("/failed/retry-all"), body);
			return parseRetryAll(response.data);
		},
		forget: async (id: string): Promise<void> => {
			await http.delete(url(`/failed/${encodeURIComponent(id)}`));
		},
		escalate: async (body: { failed_job_ids: string[]; note?: string }): Promise<EscalateResult> => {
			const response = await http.post(url("/escalate"), body);
			return parseEscalate(response.data);
		},
	};
};

export type QueueApi = ReturnType<typeof queueApi>;

export const httpStatusOf = (error: unknown): number | undefined =>
	axios.isAxiosError(error) ? error.response?.status : undefined;
